import { useQuery } from '@tanstack/react-query';

import { listHarnesses } from '@/lib/api/generated/api';

import { harnessKeys } from '../queryKeys';
import type { Harness } from '../types';

/**
 * Every Harness defined on the organization.
 *
 * Mirrors `llm-profiles/hooks/useLlmProfiles.ts`: anything but a 200 throws
 * rather than resolving to an empty list, so the caller can tell "this
 * organization has none" from "we could not find out" and never render a
 * confident empty state over a failed request. The generated `listHarnesses`
 * resolves to `{ items: HarnessView[] }`, not a bare array (the interim
 * hand-written contract's assumption) — unwrapped here.
 */
export function useHarnesses() {
  const query = useQuery({
    queryKey: harnessKeys.list(),
    queryFn: async (): Promise<Harness[]> => {
      const response = await listHarnesses();
      if (response.status !== 200) {
        throw new Error('The Harnesses could not be loaded.');
      }
      return response.data.items;
    },
    retry: false,
  });

  return {
    entries: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
    refetch: query.refetch,
  };
}
